import { useMemo } from 'react'
import type { TestRun } from '@sophia/shared-schemas'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { cn } from '@/lib/utils'

interface Props {
  run: TestRun
  classFilter: string | null
}

const NULL_KEY = '__null__'
const NULL_LABEL_EXPECTED = '(desconhecida)'
const NULL_LABEL_PREDICTED = '(não classificado)'

export function ConfusionTab({ run, classFilter }: Props) {
  const metrics = run.metrics
  const matrix = useMemo(() => {
    if (!metrics) return null
    const cells = metrics.confusion

    // Coleta classes únicas em ambos os eixos
    const expectedSet = new Set<string>()
    const predictedSet = new Set<string>()
    const nameMap = new Map<string, string>()

    for (const c of cells) {
      const ek = c.expectedClassId ?? NULL_KEY
      const pk = c.predictedClassId ?? NULL_KEY
      expectedSet.add(ek)
      predictedSet.add(pk)
      if (c.expectedClassId) nameMap.set(c.expectedClassId, c.expectedClassName)
      if (c.predictedClassId) nameMap.set(c.predictedClassId, c.predictedClassName)
    }
    // Aplica filtro de classe — mantém só rows/cols envolvendo a classe selecionada
    if (classFilter) {
      const filteredExpected = new Set([classFilter, NULL_KEY])
      const filteredPredicted = new Set([classFilter, NULL_KEY])
      for (const c of cells) {
        const ek = c.expectedClassId ?? NULL_KEY
        const pk = c.predictedClassId ?? NULL_KEY
        if (ek === classFilter || pk === classFilter) {
          filteredExpected.add(ek)
          filteredPredicted.add(pk)
        }
      }
      expectedSet.clear()
      predictedSet.clear()
      for (const e of filteredExpected) expectedSet.add(e)
      for (const p of filteredPredicted) predictedSet.add(p)
    }

    const expectedKeys = [...expectedSet].sort((a, b) => {
      if (a === NULL_KEY) return 1
      if (b === NULL_KEY) return -1
      return (nameMap.get(a) ?? '').localeCompare(nameMap.get(b) ?? '')
    })
    const predictedKeys = [...predictedSet].sort((a, b) => {
      if (a === NULL_KEY) return 1
      if (b === NULL_KEY) return -1
      return (nameMap.get(a) ?? '').localeCompare(nameMap.get(b) ?? '')
    })

    const cellMap = new Map<string, number>()
    for (const c of cells) {
      const ek = c.expectedClassId ?? NULL_KEY
      const pk = c.predictedClassId ?? NULL_KEY
      cellMap.set(`${ek}::${pk}`, c.count)
    }

    // Linha total por expected (pra normalizar a cor por linha)
    const rowTotals = new Map<string, number>()
    for (const ek of expectedKeys) {
      let total = 0
      for (const pk of predictedKeys) total += cellMap.get(`${ek}::${pk}`) ?? 0
      rowTotals.set(ek, total)
    }

    return { expectedKeys, predictedKeys, cellMap, rowTotals, nameMap }
  }, [metrics, classFilter])

  if (!metrics || !matrix) return <p className="text-sm text-fg-3">Sem dados de confusão.</p>

  function nameFor(key: string, axis: 'expected' | 'predicted'): string {
    if (key === NULL_KEY) return axis === 'expected' ? NULL_LABEL_EXPECTED : NULL_LABEL_PREDICTED
    return matrix!.nameMap.get(key) ?? '?'
  }

  function colorClass(intensity: number, isDiagonal: boolean): string {
    if (intensity === 0) return 'bg-bg-surface text-fg-3'
    if (isDiagonal) {
      if (intensity >= 0.8) return 'bg-success text-white'
      if (intensity >= 0.5) return 'bg-success/60 text-fg-1'
      if (intensity >= 0.2) return 'bg-success/30 text-fg-1'
      return 'bg-success/15 text-fg-1'
    }
    if (intensity >= 0.5) return 'bg-danger text-white'
    if (intensity >= 0.2) return 'bg-danger/60 text-white'
    if (intensity >= 0.05) return 'bg-danger/30 text-fg-1'
    return 'bg-danger/15 text-fg-2'
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="font-display font-bold text-base text-fg-1">Matriz de confusão</h3>
        <p className="text-sm text-fg-3 mt-0.5">
          Linhas = classe real (da planilha). Colunas = classe predita pela cascata. Diagonal verde = acerto.
        </p>
      </CardHeader>
      <CardBody className="p-0 overflow-x-auto">
        <table className="w-full text-xs border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="px-2 py-2 text-left text-2xs uppercase tracking-wider text-fg-3 sticky left-0 bg-bg-surface z-10">
                Real ↓ / Predita →
              </th>
              {matrix.predictedKeys.map((pk) => (
                <th key={pk} className="px-2 py-2 text-2xs text-fg-3 font-medium text-left whitespace-nowrap">
                  <div className="-rotate-45 origin-bottom-left translate-y-2 inline-block">
                    {nameFor(pk, 'predicted')}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.expectedKeys.map((ek) => {
              const total = matrix.rowTotals.get(ek) ?? 0
              return (
                <tr key={ek}>
                  <th className="px-2 py-1.5 text-left text-xs text-fg-2 font-medium sticky left-0 bg-bg-surface whitespace-nowrap">
                    {nameFor(ek, 'expected')}{' '}
                    <span className="data-mono text-2xs text-fg-3">({total})</span>
                  </th>
                  {matrix.predictedKeys.map((pk) => {
                    const v = matrix.cellMap.get(`${ek}::${pk}`) ?? 0
                    const intensity = total > 0 ? v / total : 0
                    const isDiag = ek === pk
                    return (
                      <td
                        key={pk}
                        className={cn(
                          'px-2 py-1.5 text-center data-mono border border-border-subtle',
                          colorClass(intensity, isDiag),
                        )}
                        title={`${nameFor(ek, 'expected')} → ${nameFor(pk, 'predicted')}: ${v} (${(intensity * 100).toFixed(1)}%)`}
                      >
                        {v > 0 ? v : ''}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </CardBody>
    </Card>
  )
}
